// © 2026 AttainIP Engineering
/**
 * "This call renders its own errors" — the declaration the global error sink
 * reads before it raises the banner.
 *
 * THE BUG THIS EXISTS TO KILL. A component that catches a refusal and renders
 * a precise, deliberate sentence about it ("we could not check what they own,
 * so access is left in place") was ALSO getting the QueryClient's global
 * `onError` sink firing behind it — "An unexpected error occurred. Please try
 * again." Two messages about one event, contradicting each other, and the
 * alarming one is the wrong one. The sink had no way to know the error had
 * already been answered.
 *
 * THE SHAPE OF THE FIX, AND WHY THIS SHAPE. The declaration is OPT-IN and it
 * is made where the query/mutation is DECLARED, not inferred from the error at
 * the moment it is thrown:
 *
 *  - **Opt in to handling, never opt out of reporting by default.** The banner
 *    is RFC-0202's last line of defence: a failure nobody renders must still
 *    reach the user. Anything that defaults to silence trades the wrong
 *    message for no message, which is worse.
 *  - **Not by error shape.** Mapping "guest codes" into `api/errors.ts` would
 *    have silenced this one refusal by giving it a second copy of its own
 *    message, in a second place, leaving the NEXT locally-handled error with
 *    exactly the same defect. Whether an error is handled is a property of the
 *    CALL SITE, not of the error.
 *  - **It names who handles it.** `handledLocally('GuestAccessPanel — …')`
 *    reads as a claim someone can check, and a stale one is findable by grep.
 *    An empty name does not count as a declaration (see
 *    `declaresHandledErrors`), so `meta: {}` cannot silence anything by
 *    accident.
 *
 * React Query's `meta` is the conventional carrier, and it is per query/
 * mutation instance — so a hook only declares this when EVERY consumer of that
 * hook renders the error itself. A hook with mixed consumers must not declare
 * it: the consumer that renders nothing would be left with no feedback at all.
 *
 * Pure leaf module — no imports, deliberately. It is read by the sink
 * (`store/errorSink`) and written by hooks all over the tree, so it must never
 * drag the store or the api client into a hook's import graph.
 */

/** The `meta` key. Exported so tests and greps have one spelling to use. */
export const HANDLED_ERRORS_META_KEY = 'errorsHandledBy';

/** `meta` carrying the declaration. */
export interface HandledErrorsMeta extends Record<string, unknown> {
  /** Who renders this call's errors, in prose. Never empty. */
  errorsHandledBy: string;
}

/**
 * Declare that the consumer of this query/mutation renders its own errors, so
 * the global banner must stay out of the way.
 *
 * @param renderedBy who renders it and how — e.g.
 *   `'GuestAccessPanel — every refusal via describeGuestError'`. This is
 *   documentation for the next reader, and the thing they will grep when the
 *   panel stops doing it.
 */
export function handledLocally(renderedBy: string): HandledErrorsMeta {
  return { [HANDLED_ERRORS_META_KEY]: renderedBy };
}

/**
 * Does this query/mutation declare that it owns its errors?
 *
 * Requires a non-empty name on purpose: the declaration is a claim about a
 * specific surface, and an anonymous one is indistinguishable from a mistake.
 * Anything else — a missing `meta`, a `meta` without the key, a boolean, an
 * empty string — means "not declared", and the banner fires.
 */
export function declaresHandledErrors(meta: Record<string, unknown> | undefined): boolean {
  const renderedBy = meta?.[HANDLED_ERRORS_META_KEY];
  return typeof renderedBy === 'string' && renderedBy.trim().length > 0;
}
